import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Table from '../../components/table'
import Pagination from '../../components/pagination'
import ProgramsFilter from './filter'
import { BadgeCellRenderer } from '../../components/table-cell-renderers'
import useTableDataSync from '../../infrastructure/event-bus/hooks/use-table-data-sync'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import EventBus from '../../infrastructure/event-bus/event-bus'
import general from '../../utils/general'
import RoleBasedAccess from '../../components/role-based-access'
import { UserTypeEnum } from '../../utils/enums'
import QuotaModal from '../universities/components/QuotaModal'
import AcceptedScoresModal from '../universities/components/AcceptedScoresModal'
import useI18n from '../../i18n/useI18n'

const Programs = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, language } = useI18n()
  const [programs, setPrograms] = useState([])
  const [loading, setLoading] = useState(true)
  const [isQuotaModalOpen, setIsQuotaModalOpen] = useState(false)
  const [selectedProgramForQuota, setSelectedProgramForQuota] = useState(null)
  const [isAcceptedScoresModalOpen, setIsAcceptedScoresModalOpen] = useState(false)
  const [selectedProgramForAcceptedScores, setSelectedProgramForAcceptedScores] = useState(null)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  })

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    university_id: '',
    degree: '',
    language: '',
    is_active: ''
  })


  const fetchPrograms = (page = 1, filterParams = {}, limit = null, sortParams = null) => {
    setLoading(true)

    // Build query parameters
    const queryParams = {
      page: page,
      limit: limit || pagination.limit
    }

    // Add filters if they have values
    if (filterParams.search && filterParams.search.trim()) {
      queryParams.search = filterParams.search.trim()
    }
    if (filterParams.university_id && filterParams.university_id.trim()) {
      queryParams.university_id = filterParams.university_id.trim()
    }
    if (filterParams.degree && filterParams.degree.trim()) {
      queryParams.degree = filterParams.degree.trim()
    }
    if (filterParams.language && filterParams.language.trim()) {
      queryParams.language = filterParams.language.trim()
    }
    if (filterParams.is_active !== '' && filterParams.is_active !== null) {
      queryParams.is_active = filterParams.is_active
    }

    // Add sort parameters if provided
    if (sortParams && sortParams.sort && sortParams.order) {
      queryParams.sort = sortParams.sort
      queryParams.order = sortParams.order
    }

    dispatch(
      ApiCallActions.Get({
        url: 'programs',
        query: queryParams,
        onSuccess: ({ data }) => {
          setPrograms(data.data || [])
          setPagination({
            total: data.meta?.total || 0,
            page: data.meta?.page || 1,
            limit: data.meta?.limit || 10,
            totalPages: data.meta?.totalPages || 1
          })
          setLoading(false)
        },
        onError: () => {
          setPrograms([])
          setLoading(false)
        }
      })
    )
  }

  useEffect(() => {
    fetchPrograms(1, filters)
  }, [])

  const handlePageChange = (page) => {
    fetchPrograms(page, filters)
  }

  const handleLimitChange = (newLimit) => {
    const updatedPagination = {
      ...pagination,
      limit: newLimit,
      page: 1
    }
    setPagination(updatedPagination)
    fetchPrograms(1, filters, newLimit)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleFilterSubmit = (newFilters) => {
    setFilters(newFilters)
    fetchPrograms(1, newFilters)
  }

  const handleClearFilters = (clearedFilters) => {
    setFilters(clearedFilters)
    fetchPrograms(1, clearedFilters)
  }

  const handleSort = (columnKey, direction) => {
    const sortParams = direction ? { sort: columnKey, order: direction } : null
    fetchPrograms(pagination.page, filters, pagination.limit, sortParams)
  }

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return t('COMMON_NOT_AVAILABLE')
    if (parseFloat(value) === 0) {
      return (
        <span style={{ color: '#f39c12', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <i className="zmdi zmdi-time" style={{ fontSize: '14px' }}></i>
          {t('PROGRAMS_TUITION_NOT_DISCLOSED')}
        </span>
      )
    }
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(value)
  }

  const tableColumns = [
    {
      key: 'name',
      label: t('PROGRAMS_TABLE_COLUMNS_NAME'),
      sortable: true,
      render: (value, row) => general.getLocaleValue(row, 'name', language)
    },
    {
      key: 'faculty',
      label: t('PROGRAMS_TABLE_COLUMNS_FACULTY'),
      sortable: true,
      render: (value, row) => general.getLocaleValue(row, 'faculty', language) || "-"
    },
    {
      key: 'university',
      label: t('PROGRAMS_TABLE_COLUMNS_UNIVERSITY'),
      sortable: true,
      render: (value) => general.getLocaleValue(value, 'name', language) || t('COMMON_NOT_AVAILABLE')
    },
    {
      key: 'degree',
      label: t('PROGRAMS_TABLE_COLUMNS_DEGREE'),
      sortable: true
    },
    {
      key: 'language',
      label: t('PROGRAMS_TABLE_COLUMNS_LANGUAGE'),
      sortable: true
    },
    {
      key: 'tuitionFee',
      label: t('PROGRAMS_TABLE_COLUMNS_TUITION'),
      sortable: true,
      render: (value, row) => {
        if (parseFloat(value) !== 0 && row.discountedFee && row.discountedFee < value) {
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px' }}>
                {formatCurrency(value)}
              </span>
              <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                {formatCurrency(row.discountedFee)}
              </span>
            </div>
          )
        }
        return formatCurrency(value)
      }
    },
    {
      key: 'durationYears',
      label: t('PROGRAMS_TABLE_COLUMNS_DURATION'),
      sortable: true,
      render: (value) => value ? `${value} ${t('PROGRAMS_DURATION_YEAR_SUFFIX')}` : t('COMMON_NOT_AVAILABLE')
    },
    {
      key: 'isActive',
      label: t('PROGRAMS_TABLE_COLUMNS_STATUS'),
      sortable: true,
      render: (value) => (
        <BadgeCellRenderer
          value={value}
          options={{
            trueLabel: t('PROGRAMS_TABLE_STATUS_ACTIVE'),
            falseLabel: t('PROGRAMS_TABLE_STATUS_INACTIVE'),
            trueClass: 'badge-success',
            falseClass: 'badge-danger'
          }}
        />
      )
    }
  ]

  const handleEditClick = (row) => {
    navigate(`/programs/${row.id}/edit`)
  }

  const handleDeleteClick = async (row) => {
    const confirmed = await general.confirmDelete(t('PROGRAMS_ENTITY'), row.name)

    if (!confirmed) {
      return
    }

    dispatch(
      ApiCallActions.Delete({
        url: `programs/${row.id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          EventBus.sc.onEntityDeleted({
            entityType: 'program',
            entityId: row.id
          })

          general.showDeleteSuccess(t('PROGRAMS_ENTITY'))
        }
      })
    )
  }

  const handleQuotaClick = (row) => {
    setSelectedProgramForQuota(row)
    setIsQuotaModalOpen(true)
  }

  const handleAcceptedScoresClick = (row) => {
    setSelectedProgramForAcceptedScores(row)
    setIsAcceptedScoresModalOpen(true)
  }

  const handleQuotaModalClose = () => {
    setSelectedProgramForQuota(null)
    setIsQuotaModalOpen(false)
  }

  const handleAcceptedScoresModalClose = () => {
    setSelectedProgramForAcceptedScores(null)
    setIsAcceptedScoresModalOpen(false)
  }

  // Auto-sync table data with entity events
  useTableDataSync(
    'program',
    programs,
    setPrograms,
    pagination,
    setPagination,
    (page, filterParams) => fetchPrograms(page, filterParams || filters),
    filters
  )

  const renderActions = (row) => {
    return (
      <div className="d-flex align-items-center">
        <button
          className="btn btn-sm btn-primary btn-icon btn-round m-r-5"
          onClick={() => handleAcceptedScoresClick(row)}
          title={t('PROGRAMS_ACTIONS_SCORES')}
        >
          <i className="zmdi zmdi-chart"></i>
        </button>
        <button
          className="btn btn-sm btn-info btn-icon btn-round m-r-5"
          onClick={() => handleQuotaClick(row)}
          title={t('PROGRAMS_ACTIONS_QUOTA')}
        >
          <i className="zmdi zmdi-accounts"></i>
        </button>
        <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
          <button
            className="btn btn-sm btn-warning m-r-5 btn-icon btn-round"
            onClick={() => handleEditClick(row)}
            title={t('PROGRAMS_ACTIONS_EDIT')}
          >
            <i className="zmdi zmdi-edit"></i>
          </button>
          <button
            className="btn btn-sm btn-danger btn-icon btn-round"
            onClick={() => handleDeleteClick(row)}
            title={t('PROGRAMS_ACTIONS_DELETE')}
          >
            <i className="zmdi zmdi-delete"></i>
          </button>
        </RoleBasedAccess>
      </div>
    )
  }

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>{t('PROGRAMS_LIST_TITLE')}
              <small>{t('PROGRAMS_LIST_SUBTITLE')}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
              <button
                className="btn btn-white btn-icon btn-round float-right m-l-10"
                type="button"
                onClick={() => navigate('/programs/new')}
              >
                <i className="zmdi zmdi-plus"></i>
              </button>
            </RoleBasedAccess>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <a href="/dashboard">
                  <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('COMMON_HOME')}
                </a>
              </li>
              <li className="breadcrumb-item active">{t('PROGRAMS_LIST_TITLE')}</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-12">
            {/* Filter Card */}
            <ProgramsFilter
              filters={filters}
              onFilterChange={handleFilterChange}
              onFilterSubmit={handleFilterSubmit}
              onClearFilters={handleClearFilters}
              onRefresh={() => fetchPrograms(pagination.page, filters, pagination.limit)}
              loading={loading}
            />

            {/* Table Card */}
            <Table
              columns={tableColumns}
              data={programs}
              loading={loading}
              emptyMessage={t('PROGRAMS_TABLE_EMPTY')}
              renderActions={renderActions}
              onSort={handleSort}
              pagination={pagination}
            />

            {/* Pagination Card */}
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
            <QuotaModal
              isOpen={isQuotaModalOpen}
              onClose={handleQuotaModalClose}
              program={selectedProgramForQuota}
            />
            <AcceptedScoresModal
              isOpen={isAcceptedScoresModalOpen}
              onClose={handleAcceptedScoresModalClose}
              program={selectedProgramForAcceptedScores}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default Programs

